import * as React from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, CheckCircle2, Loader2, Stethoscope } from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';

const esquema = z.object({
  email: z.string().min(1, 'Informe o e-mail.').email('Informe um e-mail válido.'),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaEsqueciSenha() {
  const [enviado, setEnviado] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(esquema), defaultValues: { email: '' } });

  const aoEnviar = handleSubmit(async (dados) => {
    setErro(null);
    try {
      await api.post('/auth/forgot-password', dados);
      setEnviado(true);
    } catch (e) {
      setErro(
        e instanceof ErroApi ? e.mensagemUsuario : 'Não foi possível concluir. Tente novamente.',
      );
    }
  });

  return (
    <main className="bg-fundo flex min-h-dvh items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="bg-marca text-marca-contraste flex size-14 items-center justify-center rounded-2xl">
            <Stethoscope className="size-7" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">KAVI</h1>
        </div>

        <div className="bg-cartao rounded-xl border p-6 shadow-sm">
          {enviado ? (
            <div className="flex flex-col gap-3">
              <CheckCircle2 className="text-sucesso size-8" aria-hidden="true" />
              <h2 className="text-lg font-semibold">Verifique seu e-mail</h2>
              <p className="text-silenciado-texto text-sm" role="status">
                Se houver uma conta ativa com o e-mail informado, enviaremos as instruções de
                redefinição em instantes. O link vale por 60 minutos.
              </p>
              <p className="text-silenciado-texto text-sm">
                Não recebeu? Confira a caixa de spam ou procure a administração do sistema.
              </p>
            </div>
          ) : (
            <form
              onSubmit={(evento) => void aoEnviar(evento)}
              noValidate
              className="flex flex-col gap-4"
            >
              <div>
                <h2 className="text-lg font-semibold">Recuperar senha</h2>
                <p className="text-silenciado-texto text-sm">
                  Informe o e-mail cadastrado e enviaremos um link para você criar uma nova senha.
                </p>
              </div>

              {erro && (
                <p role="alert" className="text-destrutivo text-sm">
                  {erro}
                </p>
              )}

              <Campo id="email" rotulo="E-mail" obrigatorio erro={errors.email?.message}>
                <Entrada type="email" autoComplete="username" autoFocus {...register('email')} />
              </Campo>

              <Botao type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
                {isSubmitting ? 'Enviando…' : 'Enviar link de redefinição'}
              </Botao>
            </form>
          )}

          <Link
            to="/login"
            className="text-silenciado-texto hover:text-fundo-texto mt-5 flex items-center justify-center gap-1.5 text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Voltar ao login
          </Link>
        </div>
      </div>
    </main>
  );
}
